import React, { useRef, useState, useEffect, useContext } from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  Dimensions,
} from 'react-native';
import Animated, {
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useNavigation } from '@react-navigation/native';
import { isTablet } from 'react-native-device-info';
import { addHours, format, isSunday, parseISO, setHours } from 'date-fns';
import HTML from 'react-native-render-html';
import { EventRegister } from 'react-native-event-listeners';
import { useToast } from 'react-native-toast-notifications';
import images from '../resources/images';
import colors from '../resources/colors';
import DataController from '../controllers/DataController';
import renderers from '../resources/renderers';
import Collapsible from 'react-native-collapsible';
import AppContext from '../navigation/Context';
import MenuContext from '../navigation/MenuContext';
import RNFS from 'react-native-fs';
import FirebaseService from '../controllers/FirebaseService';

let tagsStyles = {
  p: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 16,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 22,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
  a: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 15,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    letterSpacing: -0.1,
    color: colors.defaultText,
    textDecorationLine: 'underline',
  },
  i: {
    fontFamily: 'FiraSans-Italic',
    fontWeight: 'normal',
    fontStyle: Platform.OS === 'ios' ? 'italic' : 'normal',
    color: colors.defaultText,
  },
  b: {
    fontFamily: 'FiraSans-Bold',
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
  },
  h1: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 26,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginTop: 16,
    marginBottom: 8,
  },
  h2: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 26,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginTop: 16,
    marginBottom: 8,
  },
  tab: {
    fontFamily: 'FiraSans-Regular',
    fontWeight: 'normal',
    fontStyle: 'normal',
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
  line: {
    marginBottom: 10,
  }
};

let classesStyles = {
  biog: {
    marginTop: 8,
  },
  AdditionalLectionary: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 26,
    letterSpacing: -0.1,
    color: colors.primary,
    // marginBottom: 4,
  },
  'plus-S': {
    display: 'none',
  },
  eventReadingHeader: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 18,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.accentBlack,
    marginBottom: 16,
  },
  passageref: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.primary,
    margin: 0,
  },
}

const LectionaryButton = ({ handlePress }) => (
  <Button
    label="Lectionary"
    buttonStyle={{ backgroundColor: colors.primary }}
    textStyle={{ color: colors.accentWhite }}
    iconColor={{ tintColor: colors.accentWhite }}
    handlePress={handlePress}
  />
);

const DailyPrayerButton = ({ handlePress }) => (
  <Button
    label="Daily Prayer"
    buttonStyle={{ backgroundColor: colors.dailyPrayer }}
    textStyle={{ color: colors.accentWhite }}
    iconColor={{ tintColor: colors.accentWhite }}
    handlePress={handlePress}
  />
);

const SundayWorshipButton = ({ handlePress }) => (
  <Button
    label="Sunday Worship"
    buttonStyle={{ backgroundColor: colors.sundayWorship }}
    textStyle={{ color: colors.accentWhite }}
    iconColor={{ tintColor: colors.accentWhite }}
    handlePress={handlePress}
  />
);

const ServiceSheetButton = ({ handlePress }) => (
  <Button
    label="View Service Sheet"
    // eslint-disable-next-line react-native/no-inline-styles
    buttonStyle={{
      backgroundColor: colors.grey30,
      borderWidth: 1,
      borderColor: colors.primary,
      height: 30,
      borderRadius: 8,
      width: 153,
      minWidth: 153,
    }}
    textStyle={{ color: colors.primary }}
    iconColor={{ tintColor: colors.primary }}
    handlePress={handlePress}
    showDisclosure={false}
  />
);

const FullServiceButton = ({ handlePress }) => (
  <Button
    label="View Full Service"
    // eslint-disable-next-line react-native/no-inline-styles
    buttonStyle={{
      backgroundColor: colors.grey30,
      borderWidth: 1,
      borderColor: colors.primary,
      height: 30,
      borderRadius: 8,
      width: 139,
      minWidth: 139,
    }}
    textStyle={{ color: colors.primary }}
    iconColor={{ tintColor: colors.primary }}
    handlePress={handlePress}
    showDisclosure={false}
  />
);

const Button = ({ label, onPress, buttonStyle, textStyle, iconColor, handlePress, showDisclosure }) => {
  return (
    <TouchableOpacity
      style={[styles.serviceButton, buttonStyle]}
      activeOpacity={0.5}
      onPress={handlePress}>
      <Text style={[styles.buttonText, textStyle]} allowFontScaling={false} numberOfLines={1}>
        {label}
      </Text>
      {showDisclosure ?
        <Image
          style={[styles.buttonDisclosure, iconColor]}
          source={images.Button_Disclosure_Right_White}
        />
        : null}
    </TouchableOpacity>
  );
};

const ButtonSeparator = () => <View style={styles.serviceButtonSeparator} />;

const Reading = ({ day, reading, dayIndex, fontMultiplier, blockTitle }) => {
  const toast = useToast();
  const navigation = useNavigation();

  const tagsStyles = {
    p: {
      fontFamily: 'FiraSans-Regular',
      fontSize: 15 * fontMultiplier,
      fontWeight: 'normal',
      fontStyle: 'normal',
      lineHeight: 24 * fontMultiplier,
      letterSpacing: -0.1,
      color: colors.defaultText,
      textDecorationLine: 'none',
      marginBottom: 10,
    },
    b: {
      fontFamily: 'FiraSans-SemiBold',
      fontSize: 15 * fontMultiplier,
      fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
      fontStyle: 'normal',
      lineHeight: 24 * fontMultiplier,
      letterSpacing: -0.1,
      color: colors.defaultText,
      textDecorationLine: 'none',
      marginBottom: 10,
    },
    i: {
      fontFamily: 'FiraSans-Italic',
      fontWeight: 'normal',
      fontStyle: Platform.OS === 'ios' ? 'italic' : 'normal',
      marginBottom: 8,
      color: colors.defaultText,
    },
  };

  // let html = reading.data.replace(/<a/gm, '<p').replace(/<\/a>/gm, '</p>').replace(/<\/i>/gim, ' </i>');
  // html = html.replace(/<line><line>/gm, '<line>');

  let html = reading.data
    .replace(/<a/gm, '<p')
    .replace(/<\/a>/gm, '</p>')
    .replace(/\s<i>or<\/i>/gim, '<i> or</i>')
    .replace(/<line><line>/gm, '<line>')
    .replace(/<img (.*?)\/>/gm, '')
    .replace(/<\/i>/gim, ' </i>')
  // .replace(/<line>/gim, '')
  // .replace(/<\/line>/gim, '')

  const showToast = (text) => {
    toast.show(text, { id: 'text' });
  };

  return (
    <View style={{ flex: Dimensions.get('window').width > 500 ? 1 : -1 }}>
      <View style={styles.optionButton}>
        {day.DayOfWeek === 'Sunday' && reading.option.includes('Principal Service') ?
          <Image
            style={{
              marginStart: -8,
              width: 44,
              height: 44,
            }}
            source={images.Icon_SW_Active}
          />
          : null}
        <Text
          style={[styles.optionText, {
            fontSize: 15 * fontMultiplier,
            lineHeight: 20 * fontMultiplier,
          }]}>
          {reading.option}
        </Text>
        <Image style={styles.disclosure} source={images.Button_Disclosure_Right_Grey} />
      </View>
      <HTML
        baseFontStyle={{
          fontSize: 15 * fontMultiplier,
          lineHeight: 20 * fontMultiplier,
        }}
        renderersProps={{
          fontMultiplier,
        }}
        renderers={renderers}
        tagsStyles={tagsStyles}
        source={{ html: html }}
        defaultTextProps={{ selectable: true }}
      />
      {day.DayOfWeek === 'Sunday' && (reading.option.includes('Principal Service') || reading.option.includes('Holy Communion')) ?
        <ServiceSheetButton
          handlePress={() => {
            !__DEV__ && FirebaseService.logEvent('dashboard_service_sheet', { type: blockTitle });
            !__DEV__ && FirebaseService.logEvent('day_viewed', { date: day.Date });
            navigation.navigate('Day', { day, dayIndex, openServiceSheet: true });
          }}
        />
        : null}

      {/* {reading.option.includes('Prayer') ?
        <FullServiceButton handlePress={() => showToast('View Full Service not implemented')} />
        : null} */}
    </View>
  );
};

const ServicesAndroid = ({ children }) => {
  const ref = useAnimatedRef();
  const open = useSharedValue(false);
  const height = useSharedValue(0);
  const transition = useDerivedValue(() => {
    return open.value ? withTiming(1) : withTiming(0);
  });

  const heightStyles = useAnimatedStyle(() => {
    return {
      height: 1 + transition.value * height.value,
      opacity: transition.value === 0 ? 0 : 1,
      overflow: 'hidden',
    };
  });

  const handlePress = () => {
    if (height.value === 0) {
      runOnUI(() => {
        'worklet';
        height.value = measure(ref).height;
        // console.log(height.value);
      })();
    }

    // console.log(open);
    open.value = !open.value;
  };

  return (
    <Animated.View style={styles.serviceContainer}>
      <Animated.View style={heightStyles}>
        <View ref={ref} removeClippedSubviews={false}>
          <View style={styles.separator} />
          <View
            style={{
              paddingTop: 5,
              paddingBottom: 20,
              flexDirection: isTablet() || Platform.OS === 'web' ? 'row' : 'column',
            }}>
            {children}
          </View>
        </View>
      </Animated.View>
      <View>
        <View style={styles.separator} />
        <TouchableOpacity style={styles.dropdownButton} activeOpacity={0.5} onPress={handlePress}>
          <Text style={styles.showText} allowFontScaling={false}>
            {open.value ? 'Hide services' : 'Show services'}
          </Text>
          <Image
            style={styles.dropdownIcon}
            source={open.value ? images.Button_Hide : images.Button_Show}
          />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

const Collect = ({ collect, changeCollect, collectOption, canEdit }) => {
  const toast = useToast();

  const copyPressed = () => {
    toast.show('Not implemented', { id: Date.now() });
  };

  // if (false && Platform.OS === 'android') {
  //   return (
  //     <DropdownAndroid
  //       label={`Collect`}
  //       canEdit={canEdit}
  //       editText={`Change to Collect ${collectOption === 1 ? 2 : 1}`}
  //       copyPressed={copyPressed}
  //       editPressed={changeCollect}>
  //       <HTML
  //         baseFontStyle={tagsStyles.p}
  //         tagsStyles={tagsStyles}
  //         source={{ html: collect.data }}
  //         defaultTextProps={{ selectable: true }}
  //       />
  //     </DropdownAndroid>
  //   );
  // }

  return (
    <Dropdown
      label={`Collect`}
      canEdit={canEdit}
      editText={`Change to Collect ${collectOption === 1 ? 2 : 1}`}
      copyPressed={copyPressed}
      editPressed={changeCollect}>
      <HTML
        baseFontStyle={tagsStyles.p}
        tagsStyles={tagsStyles}
        source={{ html: collect.data }}
        defaultTextProps={{ selectable: true }}
      />
    </Dropdown>
  );
};

const Events = ({ events }) => {
  return (
    <View style={styles.eventContainer}>
      {events.map((event) => (
        <Event event={event} key={`${event.data}-${event.option}`} />
      ))}
    </View>
  );
};

const Event = ({ event }) => {
  if (!DataController.eventShouldExpand(event.option)) {
    return (
      <NoDropdown label={`${event.data}${DataController.eventShouldShowOption(event.option) ? ` (${event.option})` : ''}`} />
    );
  }

  const toast = useToast();
  const [modalVisible, setModalVisible] = useState(false);
  const [bibleReading, setBibleReading] = useState(null);

  const readingTagsStyles = {
    p: styles.a,
    b: styles.a,
    a: styles.a,
  };

  const readingTags = {
    h2: tagsStyles.h2,
  };

  const readingClassStyles = {
    passageref: classesStyles.passageref,
    pn: classesStyles.passageref,
    ww: styles.ww,
    'plus-S': classesStyles['plus-S'],
    fnote: classesStyles['plus-S'],
    eventReadingHeader: classesStyles.eventReadingHeader,
  };

  const onLinkPress = async (event, href) => {
    const ref = href.replace('BibleRef/', '').replace('.html', '');
    const file = bibleHTML[`${ref}`];

    try {
      let uri;
      let html;

      if (Platform.OS === 'web') {
        html = file;
      } else {
        uri = Image.resolveAssetSource(file).uri;

        if (__DEV__) {
          html = await fetch(uri);
          html = await html.text();
        } else if (Platform.OS === 'ios') {
          // Release version - don't decode uri
          html = await RNFS.readFile(uri, 'utf8');
          // Debug version - need to decode uri
          // html = await RNFS.readFile(decodeURIComponent(uri), 'utf8');
        } else if (Platform.OS === 'android') {
          html = await RNFS.readFile(`android.resource://com.aimer.lectionary/raw/${uri}`, 'utf8');
          // html = await RNFS.readFile(`android.resource://com.aimer.lectionary.TEST/raw/${uri}`, 'utf8');
        }
      }

      html = html.replace(/<p>\s+<\/p>/gim, '').replace('<p>', '').replace(/<sup class=\"fnote\">\*<\/sup>/gim, ' ');

      setBibleReading(html);
    } catch (error) {
      console.log(error);
    }

    setModalVisible(true);
  };

  const copyPressed = () => {
    toast.show('Not implemented', { id: Date.now() });
  };

  const editPressed = () => {
    toast.show('Not implemented', { id: Date.now() });
  };

  const eventCopyright = '<p class=biog>Extract from <i>Saints on Earth: A biographical companion to Common Worship</i> by John H Darch and Stuart K Burns</p>';

  const html = `
${event.EventReadings ? '<sup class="eventReadingHeader" >' + event.EventReadings[0].option + '</sup>' + '<line>' + event.EventReadings[0].data.replace(/<i>also<\/i>/gim, '<i>also </i>').replace(/<img src=\'blueArrow.png\' class=\'BibleLinkImage\' \/>/gm, '').replace(/>\s+/gim, '>') + '</line>' : ''}
${event.EventCollect ? '<h1>Collect</h1>' + event.EventCollect[0].data.replace(/>\s+/gim, '>') : ''}
${event['Event Post Communion prayer'] ? '<h1>Post Communion Prayer</h1>' + event['Event Post Communion prayer'][0].data.replace(/>\s+/gim, '>') : ''}
${event['Event biog'] ? '<h1>Biography</h1>' + event['Event biog'][0].data + eventCopyright : ''}
`;

  const closeModal = () => setModalVisible(false);

  return (
    <Dropdown
      label={`${event.data}${DataController.eventShouldShowOption(event.option) ? ` (${event.option})` : ''}`}
      copyPressed={copyPressed}
      eventColour={event.EventsColour}>
      <HTML
        renderersProps={{
          fontMultiplier: 0,
        }}
        renderers={renderers}
        baseFontStyle={tagsStyles.p}
        tagsStyles={tagsStyles}
        classesStyles={classesStyles}
        source={{ html: html }}
        defaultTextProps={{ selectable: true }}
        onLinkPress={(_event, href) => onLinkPress(_event, href)}
      />
    </Dropdown>
  );
};

const PostCommunionPrayer = ({ prayer }) => {
  const copyPressed = () => {
    __DEV__ && console.log('copyPressed', prayer);
  };

  return (
    <Dropdown
      label="Post Communion Prayer"
      canEdit={false}
      copyPressed={copyPressed}>
      <HTML
        tagsStyles={tagsStyles}
        source={{ html: prayer.data }}
        defaultTextProps={{ selectable: true }}
      />
    </Dropdown>
  );
};

const AdditionalWeekdayLectionary = ({ reading, fontMultiplier }) => {
  const [bibleReading, setBibleReading] = useState(null);

  const readingTagsStyles = {
    p: styles.a,
    b: styles.a,
    a: styles.a,
  };

  const readingTags = {
    h2: tagsStyles.h2
  };

  const readingClassStyles = {
    passageref: classesStyles.passageref,
    pn: classesStyles.passageref,
    ww: styles.ww,
    'plus-S': classesStyles['plus-S'],
    fnote: classesStyles['plus-S'],
  };

  const copyPressed = () => {
    console.log('copyPressed', reading);
  };

  const onLinkPress = async (event, href) => {
    const ref = href.replace('BibleRef/', '').replace('.html', '');
    const file = bibleHTML[`${ref}`];

    try {
      let uri;
      let html;

      if (Platform.OS === 'web') {
        html = file;
      } else {
        uri = Image.resolveAssetSource(file).uri;

        if (__DEV__) {
          html = await fetch(uri);
          html = await html.text();
        } else if (Platform.OS === 'ios') {
          // Release version - don't decode uri
          html = await RNFS.readFile(uri, 'utf8');
          // Debug version - need to decode uri
          // const newLocal = html = await RNFS.readFile(decodeURIComponent(uri), 'utf8');
        } else if (Platform.OS === 'android') {
          html = await RNFS.readFile(`android.resource://com.aimer.lectionary/raw/${uri}`, 'utf8');
          // html = await RNFS.readFile(`android.resource://com.aimer.lectionary.TEST/raw/${uri}`, 'utf8');
        }
      }

      html = html.replace(/<p>\s+<\/p>/gim, '').replace('<p>', '').replace(/<sup class=\"fnote\">\*<\/sup>/gim, ' ');

      setBibleReading(html);
    } catch (error) {
      console.log(error);
    }

    setModalVisible(true);
  };

  const closeModal = () => setModalVisible(false);

  let html = reading.data.replace(/<a/gm, '<p').replace(/<\/a>/gm, '</p>').replace(/<\/i>/gim, ' </i>');

  return (
    <>
      <Dropdown label="Additional Weekday Lectionary" canEdit={false} copyPressed={copyPressed}>
        <HTML
          tagsStyles={tagsStyles}
          source={{ html }}
          onLinkPress={(event, href) => onLinkPress(event, href)}
          defaultTextProps={{ selectable: true }}
        />
      </Dropdown>
    </>
  );
};

const DropdownAndroid = ({ children, canEdit, editText, label, copyPressed, editPressed, eventColour }) => {
  const ref = useAnimatedRef();
  const open = useSharedValue(false);
  const offset = useSharedValue(0);
  const height = useSharedValue(0);
  const transition = useDerivedValue(() => {
    return open.value ? withTiming(1) : withTiming(0);
  });

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${offset.value}deg` }],
    };
  });

  const heightStyles = useAnimatedStyle(() => {
    return {
      height: 1 + transition.value * height.value,
      opacity: transition.value === 0 ? 0 : 1,
      overflow: Platform.OS === 'android' ? 'hidden' : 'visible',
    };
  });

  const handlePress = () => {
    if (height.value === 0) {
      runOnUI(() => {
        'worklet';
        height.value = measure(ref).height + 32;
        // console.log(height.value);
      })();
    }

    offset.value = withTiming(open.value ? 0 : 90);
    open.value = !open.value;
  };

  const containerBorder = {
    borderBottomStartRadius: open ? 0 : 4,
    borderBottomEndRadius: open ? 0 : 4,
  };

  return (
    <Animated.View style={styles.notesContainer}>
      <TouchableOpacity
        style={[styles.dropdown, containerBorder]}
        activeOpacity={0.5}
        onPress={handlePress}>
        <Animated.View style={animatedStyles}>
          <Image style={styles.dropdownIcon} source={images.Button_Expand_Red} />
        </Animated.View>
        <Text style={styles.dropdownLabel} allowFontScaling={false}>
          {label}
        </Text>
        {eventColour && (
          <View style={styles.eventColorContainer}>
            <Text style={styles.eventColorText} allowFontScaling={false}>
              {eventColour[0].data}
            </Text>
          </View>
        )}
      </TouchableOpacity>
      <Animated.View style={heightStyles}>
        <View style={styles.dropdownContainer} ref={ref}>
          {children}
        </View>
      </Animated.View>
    </Animated.View>
  );
};

const NoDropdown = ({ label, dropdownLabelStyle }) => (
  <View style={styles.notesContainer}>
    <View style={styles.dropdown}>
      <Text style={[styles.noDropdownLabel, dropdownLabelStyle]} allowFontScaling={false}>
        {label}
      </Text>
    </View>
  </View>
);

const Dropdown = ({ dropdownOpen, children, canEdit, editText, label, copyPressed, editPressed, eventColour, dropdownLabelStyle }) => {
  // const openAnim = useRef(new ReactAnimated.Value(0)).current;
  const disclosureAnim = useRef(new ReactAnimated.Value(0)).current;
  const [open, setOpen] = useState(false);
  const [dimensions, setDimensions] = useState(0);

  useEffect(() => {
    if (dropdownOpen) {
      expandOpen();
      setOpen(!open);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    EventRegister.addEventListener('day/close-dropdowns', () => {
      expandClose();
      setOpen(false);
    });

    return () => EventRegister.removeEventListener('day/close-dropdowns');
  }, []);

  const expandOpen = (value) => {
    // ReactAnimated.spring(openAnim, {
    //   toValue: dimensions,
    //   friction: 50,
    //   useNativeDriver: false,
    // }).start();

    ReactAnimated.spring(disclosureAnim, {
      toValue: 90,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const expandClose = () => {
    // ReactAnimated.spring(openAnim, {
    //   toValue: 0,
    //   friction: 50,
    //   useNativeDriver: false,
    // }).start();

    ReactAnimated.spring(disclosureAnim, {
      toValue: 0,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const handlePress = () => {
    if (open) {
      expandClose();
    } else {
      expandOpen();
    }

    setOpen(!open);
  };

  const measureDimensions = (event) => {
    const _height = event.nativeEvent.layout.height;
    setDimensions(_height);
  };

  // const heightStyles = {
  //   overflow: Platform.OS === 'ios' ? 'scroll' : 'hidden',
  //   height: openAnim,
  // };

  const animatedStyles = {
    transform: [
      {
        rotate: disclosureAnim.interpolate({
          inputRange: [0, 90],
          outputRange: ['0deg', '90deg'],
        }),
      },
    ],
  };

  const containerBorder = {
    borderBottomStartRadius: open ? 0 : 4,
    borderBottomEndRadius: open ? 0 : 4,
  };

  let background =
    eventColour ? eventColour[0].data === 'Hangings removed' ? 'grey' : eventColour[0].data.split(' ')[0].toLowerCase() : null;
  const backgroundHex = background && DataController.colourNameToHex(background);
  const textColor = backgroundHex ? DataController.getTextColor(backgroundHex) : colors.accentWhite;

  return (
    <ReactAnimated.View style={styles.notesContainer}>
      <TouchableOpacity
        style={[styles.dropdown, containerBorder]}
        activeOpacity={0.5}
        onPress={handlePress}>
        <ReactAnimated.View style={animatedStyles}>
          <Image style={styles.dropdownIcon} source={images.Button_Expand_Red} />
        </ReactAnimated.View>
        <Text style={[styles.dropdownLabel, dropdownLabelStyle]} allowFontScaling={false}>
          {label}
        </Text>
        {eventColour && (
          <View
            style={[
              styles.eventColorContainer,
              {
                height: background === 'White' ? 18 : 16,
                backgroundColor: backgroundHex ? backgroundHex : 'grey',
                borderWidth: background === 'White' ? 1 : 0,
              },
            ]}>
            <Text
              style={[styles.eventColorText, { color: textColor }]}
              allowFontScaling={false}>
              {eventColour[0].data}
            </Text>
          </View>
        )}
      </TouchableOpacity>
      <Collapsible collapsed={!open}>
        <View style={styles.dropdownContainer} onLayout={(event) => measureDimensions(event)}>
          {children}
        </View>
      </Collapsible>
    </ReactAnimated.View>
  );
};

const Services = ({ children, initiallyOpen }) => {
  const openAnim = useRef(new ReactAnimated.Value(0)).current;
  const [open, setOpen] = useState(initiallyOpen);
  const [readingsHeight, setReadingsHeight] = useState(0);

  useEffect(() => {
    setTimeout(() => {
      if (open) {
        openAnim.setValue(readingsHeight);
      }
    }, 10);
  }, [readingsHeight]);

  const expandOpen = (value) => {
    ReactAnimated.spring(openAnim, {
      toValue: readingsHeight,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const expandClose = () => {
    ReactAnimated.spring(openAnim, {
      toValue: 0,
      friction: 50,
      useNativeDriver: false,
    }).start();
  };

  const buttonPressed = () => {
    if (open) {
      expandClose();
    } else {
      expandOpen();
    }

    setOpen(!open);
  };

  const measureLayout = (event) => {
    const { height } = event.nativeEvent.layout;

    setReadingsHeight(height);
  };

  return (
    <ReactAnimated.View style={styles.serviceContainer}>
      <Collapsible collapsed={!open}>
        <View style={styles.separator} />
        {children}
      </Collapsible>
      <View>
        <View style={styles.separator} />
        <TouchableOpacity style={styles.dropdownButton} activeOpacity={0.5} onPress={buttonPressed}>
          <Text style={styles.showText} allowFontScaling={false}>
            {open ? 'Hide services' : 'Show services'}
          </Text>
          <Image
            style={styles.dropdownIcon}
            source={open ? images.Button_Hide : images.Button_Show}
          />
        </TouchableOpacity>
      </View>
    </ReactAnimated.View>
  );
};

const ServiceDropdown = ({ showDropdowns, day, dayIndex, open, blockTitle }) => {
  const menuOpen = useContext(MenuContext);

  const navigation = useNavigation();
  const sunday = isSunday(new Date(day.Date));
  const toast = useToast();
  const [fontMultiplier, setFontMultiplier] = useState(1);

  const getFontMultiplier = async () => {
    const fontMultiplierInStorage = await DataController.getFontMultiplier();
    setFontMultiplier(fontMultiplierInStorage);
  };

  const showToast = (text) => {
    toast.show(text, { id: 'text' });
  };

  useEffect(() => {
    getFontMultiplier();
  }, [])

  useEffect(() => {
    EventRegister.addEventListener('settings/font-size-updated', getFontMultiplier);

    return () => EventRegister.removeEventListener('settings/font-size-updated');
  }, []);

  let background =
    day.Colour && (day.Colour === 'Hangings removed' ? 'grey' : day.Colour.split(/[ |\/]/gmi)[0].toLowerCase());
  const backgroundHex = DataController.colourNameToHex(background);
  const textColor = backgroundHex ? DataController.getTextColor(backgroundHex) : colors.accentWhite;

  const collect = day.Collect !== undefined;
  const postCommunionPrayer = day['Post Communion prayer'] !== undefined;
  const event = day.Event;
  const additionalLectionary = day.AdditionalWeekdayLectionary;

  const offset = new Date().getTimezoneOffset() * 60 * 1000;
  const time = new Date(new Date(day.Date));
  __DEV__ && console.log('ServiceDropdown', time);
  __DEV__ && console.log('ServiceDropdown', day.Date);
  __DEV__ && console.log('ServiceDropdown', parseISO(day.Date).toUTCString());

  return (
    <Animated.View style={[styles.container, {
      width: isTablet() || Platform.OS === 'web'
        ? Dimensions.get('window').width - 60
        : Dimensions.get('window').width - 30,
      overflow: 'hidden',
    }]}>
      <TouchableOpacity
        onPress={() => {
          !__DEV__ && FirebaseService.logEvent('dashboard_card', { type: blockTitle });
          !__DEV__ && FirebaseService.logEvent('day_viewed', { date: day.Date });
          navigation.navigate('Day', { day, dayIndex });
        }}
        style={styles.headerContainer}>
        <View>
          <View style={{ flex: 1, flexDirection: 'row', maxWidth: Dimensions.get('window').width > 800 || showDropdowns ? '100%' : '55%', }}>
            <Text
              style={[styles.weekTitle, {
                fontSize: 12 * fontMultiplier,
                lineHeight: 16 * fontMultiplier,
              }]}
              allowFontScaling={false}>
              {day.WeekTitle}
            </Text>
            {event && (
              <View style={{ flexDirection: 'row', flex: 1, }}>
                <View
                  style={{
                    marginHorizontal: 6,
                    height: 14 * fontMultiplier,
                    width: 1,
                    backgroundColor: colors.grey50,
                  }}
                />
                <Text
                  style={[styles.weekTitle, {
                    fontSize: 12 * fontMultiplier,
                    lineHeight: 16 * fontMultiplier,
                  }]}
                  numberOfLines={1}
                  allowFontScaling={false}>
                  {event[0].data}
                </Text>
              </View>
            )}
          </View>
          {(showDropdowns && day.Colour) && (
            <View style={[
              styles.colorContainer,
              {
                height: day.Colour === 'White' ? 18 : 16,
                backgroundColor: backgroundHex ? backgroundHex : 'grey',
                borderWidth: day.Colour === 'White' ? 1 : 0,
                alignSelf: 'flex-start',
                marginVertical: 8,
              },
            ]}>
              <Text style={[styles.colorText, { color: textColor }]} allowFontScaling={false}>
                {day.Colour}
              </Text>
            </View>
          )}
          <View
            style={{ marginTop: -4, marginBottom: -4, flexDirection: 'row', alignItems: 'center' }}>
            <Text
              style={[styles.date, {
                fontSize: 20 * fontMultiplier,
                lineHeight: 28 * fontMultiplier,
                maxWidth: Dimensions.get('window').width > 800 ? '90%' : '85%',
              }]}
              allowFontScaling={false}>
              {format(parseISO(day.Date), 'EEEE d MMMM yyyy')}
            </Text>
            <Image
              source={images.Button_Disclosure_Circle_Right}
              style={{ height: 44, width: 44 }}
            />
          </View>
          {day.DayTitle && (
            <Text
              style={[styles.dayTitle, {
                fontSize: 15 * fontMultiplier,
                lineHeight: 20 * fontMultiplier,
              }]}
              allowFontScaling={false}>
              {day.DayTitle}
            </Text>
          )}
        </View>
        {(!showDropdowns && day.Colour) && (
          <View style={[
            styles.colorContainer,
            {
              height: day.Colour === 'White' ? 18 : 16,
              backgroundColor: backgroundHex ? backgroundHex : 'grey',
              borderWidth: day.Colour === 'White' ? 1 : 0,
              alignSelf: 'flex-start',
              position: 'absolute',
              right: 0,
            },
          ]}>
            <Text style={[styles.colorText, { color: textColor }]} allowFontScaling={false}>
              {day.Colour}
            </Text>
          </View>
        )}
      </TouchableOpacity>
      {day.Readings && (
        <Services initiallyOpen={open}>
          <View
            style={{
              flexDirection: Dimensions.get('window').width > 500 ? 'row' : 'column',
            }}>
            {day.Readings.map((reading, index) => (
              <Reading
                day={day}
                blockTitle={blockTitle}
                dayIndex={dayIndex}
                reading={reading}
                key={`${reading.option}-${index}`}
                fontMultiplier={fontMultiplier}
              />
            ))}
          </View>
        </Services>
      )}

      {showDropdowns && (
        <View style={{ marginTop: -15, marginBottom: 7, }}>
          {collect && (
            <Collect
              changeCollect={() => console.log(1)}
              canEdit={false}
              collectOption={1}
              collect={day.Collect[0]}
            />
          )}
          {postCommunionPrayer && (
            <PostCommunionPrayer prayer={day['Post Communion prayer'][0]} />
          )}
          {additionalLectionary && (
            <AdditionalWeekdayLectionary reading={additionalLectionary[0]} fontMultiplier={fontMultiplier} />
          )}
        </View>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    backgroundColor: colors.accentWhite,
    borderWidth: 1,
    borderColor: colors.grey40,
    marginTop: 10,
    marginBottom: 15,
    padding: 15,
    paddingBottom: 9,
    maxWidth: 1400,
    alignSelf: 'center',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  button: {
    height: 44,
    borderRadius: 4,
  },
  weekTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.2,
    color: colors.defaultText,
  },
  date: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.defaultText,
  },
  dayTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    letterSpacing: 0,
    color: colors.defaultText,
  },
  colorContainer: {
    borderRadius: 9,
    backgroundColor: '#009900',
    flex: -1,
    height: 16,
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  colorText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.1,
    color: colors.accentWhite,
  },
  serviceContainer: {
    flex: -1,
    marginVertical: 15,
  },
  separator: {
    height: 2,
    backgroundColor: colors.red,
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 36,
    backgroundColor: colors.grey20,
    shadowColor: colors.grey40,
    shadowOffset: {
      width: 0,
      height: -1,
    },
    shadowRadius: 0,
    shadowOpacity: 1,
  },
  showText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 13,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    letterSpacing: 0,
    color: colors.red,
  },
  dropdownIcon: {
    height: 44,
    width: 44,
    marginStart: -12,
  },
  optionButton: {
    flex: -1,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
  },
  disclosure: {
    flex: -1,
    height: 44,
    width: 44,
    marginStart: -12,
    tintColor: 'white',
  },
  optionText: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    letterSpacing: 0,
    color: colors.red,
  },
  a: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 15,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.defaultText,
    textDecorationLine: 'none',
    marginBottom: 10,
  },
  i: {
    fontFamily: 'FiraSans-Italic',
    fontWeight: 'normal',
    fontStyle: Platform.OS === 'ios' ? 'italic' : 'normal',
    marginBottom: 8,
    color: colors.defaultText,
  },
  servicesContainer: {
    // flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  serviceButton: {
    alignSelf: isTablet() || Platform.OS === 'web' ? 'flex-start' : 'flex-start',
    width: isTablet() || Platform.OS === 'web' ? 154 : 300,
    maxWidth: '49%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: 44,
    borderRadius: 4,
    marginBottom: 11,
  },
  serviceButtonSeparator: {
    width: 6,
  },
  buttonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    letterSpacing: 0,
    textAlign: 'center',
  },
  buttonDisclosure: {
    height: 44,
    width: 44,
    position: 'absolute',
    right: 0,
  },
  notesContainer: {
    marginTop: 15,
  },
  dropdown: {
    minHeight: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 4,
    backgroundColor: colors.grey30,
    borderWidth: 1,
    borderColor: colors.grey40,
  },
  dropdownIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  dropdownLabel: {
    flex: 1,
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginStart: -5,
    paddingVertical: 10,
    marginEnd: 8,
  },
  noDropdownLabel: {
    flex: 1,
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginStart: 18,
  },
  dropdownContainer: {
    borderColor: colors.grey40,
    backgroundColor: colors.accentWhite,
    borderBottomStartRadius: 4,
    borderBottomEndRadius: 4,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    paddingHorizontal: 15,
    paddingTop: 12,
    paddingBottom: 10,
  },
});

export default ServiceDropdown;
